/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import './index.css';

import {
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  LexicalNode,
  SELECTION_CHANGE_COMMAND,
  TextNode,
} from 'lexical';
import * as React from 'react';
import {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { getSelectedNode } from '../../utils/getSelectedNode';
import { setFloatingElemPositionForLinkEditor } from '../../utils/setFloatingElemPositionForLinkEditor';
import { makeUrlValid, sanitizeUrl } from '../../utils/url';
import { FilesClient, getBaseUrl } from 'services/api/api-client';
import { AppLink } from '../../../../../../components/uikit/buttons/AppLink.tsx';
import UploadIcon from 'assets/icons/upload.svg?react';
import ConfirmIcon from 'assets/icons/success.svg?react';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import TrashIcon from 'assets/icons/bin.svg?react';
import EditIcon from 'assets/icons/edit-grey.svg?react';
import clsx from 'clsx';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { errorToString } from 'helpers/error-helpers.ts';
import { LinearProgress } from '@mui/material';
import { AxiosProgressEvent } from 'axios';
import { PageDesignerContext } from '../../../DesignerNewPage.tsx';
import { $isLocalizationLinkNode } from '../../nodes/Localizable/LocalizationLinkNode.ts';
import { useSettings } from '../../context/SettingsContext.tsx';

const fileSpanStyles = 'position:relative; display:inline';

const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};
const sharepointBaseUrl = 'https://loewensteinmedical.sharepoint.com';
const apiFiles = '/api/files/';

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
  linkEditorRef,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  linkEditorRef: React.RefObject<HTMLDivElement | null>;
}): React.JSX.Element {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null,
  );
  const [fileDescription, setFileDescription] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isSelectionInsideTabLink, setIsSelectionInsideTabLink] =
    useState(false);

  const i18n = useScopedTranslation('Page.PageDesigner');
  const pageDesignerContext = useContext(PageDesignerContext);

  const { setOption: setSetting } = useSettings();

  const $updateLinkEditor = useCallback(() => {
    let selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        const linkParentUrl = linkParent.getURL();
        setLinkUrl(linkParentUrl);
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
      if (isLinkEditMode) {
        if (linkUrl !== 'https://') {
          setEditedLinkUrl(linkUrl);
        }
      }

      const anchorNode = selection.anchor.getNode();
      const parentNode = anchorNode.getParent();

      if ($isLocalizationLinkNode(parentNode)) {
        if (parentNode.isInsideTab()) {
          setIsSelectionInsideTabLink(true);
        } else if ($isRangeSelection(lastSelection)) {
          try {
            const previousNode = lastSelection.anchor.getNode().getParent();

            if (
              !parentNode.isInsideTab() &&
              $isLocalizationLinkNode(previousNode) &&
              previousNode.isInsideTab()
            ) {
              setIsSelectionInsideTabLink(false);
            }
          } catch (e) {}
        }
      } else {
        setIsSelectionInsideTabLink(false);
      }
    }
    const editorElem = linkEditorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(
          domRect,
          editorElem,
          anchorElem,
          undefined,
          undefined,
          isSelectionInsideTabLink,
        );
      }
      setLastSelection(selection);
    } else if (
      !activeElement ||
      !activeElement.className.includes('link-input')
    ) {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, $updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            setSetting('isCreatingLink', false, false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, $updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateLinkEditor();
    });
  }, [editor, $updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorTextAreaInteraction = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleLinkSubmission(editedLinkUrl, fileDescription);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = async (
    editedLinkUrl: string,
    fileDescription: string | null,
  ) => {
    if (editedLinkUrl.startsWith(sharepointBaseUrl)) {
      const result = await addSharepointUrl(editedLinkUrl);
      if (!result) return;
      editedLinkUrl = result.url;
      fileDescription = result.fileDescription;
    }

    if (lastSelection !== null) {
      if (editedLinkUrl !== '') {
        editor.update(() => {
          let selection = $getSelection();

          if (!selection && $isRangeSelection(lastSelection)) {
            const focusNode = lastSelection.focus.getNode();
            focusNode.select();
            selection = $getSelection();
          }

          if (!selection || !$isRangeSelection(selection)) return;

          if (!editedLinkUrl.includes(apiFiles) || fileDescription === null) {
            return;
          } else {
            editedLinkUrl = apiFiles + editedLinkUrl.split(apiFiles)[1];
          }

          const paragraphNode = $createParagraphNode();

          // we need this setStyle because only this way file info will be in a separate span,
          // and that's important for adding className to that separate span
          let textNode: TextNode | LexicalNode = $createTextNode(
            '\n' + (fileDescription ?? ''),
          ).setStyle(fileSpanStyles);

          paragraphNode.append(textNode);

          // it's not duplicate, we need this because of link's rework. Previously when this code was running
          // node was already automatically converted to link, now we need to do it manually
          // in order to make sure that "parent" below is link
          editor.dispatchCommand(
            TOGGLE_LINK_COMMAND,
            makeUrlValid(sanitizeUrl(editedLinkUrl)),
          );

          if ($isRangeSelection(lastSelection)) {
            const parent = getSelectedNode(lastSelection).getParent();
            const children = parent?.getChildren();

            if ($isLinkNode(parent)) {
              children?.forEach((child, index) => {
                if (index > 0) {
                  child.remove();
                }
              });
            }

            parent?.append(textNode);
          }

          if (!selection) {
            textNode.selectStart();
          }
        });

        editor.dispatchCommand(
          TOGGLE_LINK_COMMAND,
          makeUrlValid(sanitizeUrl(editedLinkUrl)),
        );
      } else {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
      setEditedLinkUrl('');
      setIsLinkEditMode(false);
    }
    setIsLinkEditMode(false);
    setFileDescription(null);
  };
  const modals = useModal();
  const addSharepointUrl = async (
    sharepointUrl: string,
  ): Promise<{ url: string; fileDescription: string } | null> => {
    try {
      setIsLoading(true);
      const result = await FilesClient.uploadSharepoint({
        url: sharepointUrl,
        externalId: null,
      });

      const baseUrl = getBaseUrl() || window.location.origin;
      const newUrl = `${baseUrl}${apiFiles}${result.id}`;
      const description =
        result.fileName.split('.').at(-1) + ', ' + formatBytes(result.size);

      return { fileDescription: description, url: newUrl };
    } catch (e: any) {
      alert(errorToString(e));
    } finally {
      setIsLoading(false);
    }
    return null;
  };

  const showAddSharepointFileModal = async () => {
    const sharepointUrl = await modals.showPrompt({
      title: 'Enter sharepoint link',
      fieldName: 'Link',
      text: '',
      defaultValue: '',
      onSubmit(value) {
        if (!value.startsWith(sharepointBaseUrl))
          return 'Not a valid sharepoint URL';
        return true;
      },
    });
    if (!sharepointUrl) return;

    const result = await addSharepointUrl(sharepointUrl);
    if (result === null) return;
    setEditedLinkUrl(result.url);
    setFileDescription(result.fileDescription);
  };

  const uploadFileRef = useRef<HTMLInputElement>(null);
  const triggerFileUpload = () => {
    if (uploadFileRef.current) {
      uploadFileRef.current.click();
    }
  };
  const handleFileUpload = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) {
      return;
    }

    setFileDescription(
      file.name.split('.').at(-1) + ', ' + formatBytes(file.size),
    );

    try {
      setIsLoading(true);
      const result = await FilesClient.uploadFile(
        false,
        undefined,
        {
          data: file,
          fileName: file.name,
        },
        {
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100,
              );
              setUploadProgress(percent);
            }
          },
        },
      );

      const baseUrl = getBaseUrl() || window.location.origin;
      setEditedLinkUrl(`${baseUrl}/api/files/${result.id}`);
    } catch (e: any) {
      alert(errorToString(e));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setUploadProgress(null);
      }, 400);
    }
  };
  return (
    <div ref={linkEditorRef} className="link-editor">
      {!isLink ? null : isLinkEditMode ? (
        <div className={'textAreaContainer'}>
          <textarea
            ref={textAreaRef}
            className={clsx('link-input', isLoading && 'opacity')}
            value={editedLinkUrl}
            onChange={(event) => {
              setEditedLinkUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              monitorTextAreaInteraction(event);
            }}
          />
          <div className="link-container">
            {!isSelectionInsideTabLink && (
              <div
                className={clsx('link-upload', isLoading && 'disabledLink')}
                role="button"
                tabIndex={0}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (isLoading) return;
                  triggerFileUpload();
                }}
              >
                <UploadIcon />
              </div>
            )}
            <input
              type="file"
              id="uploadFile"
              ref={uploadFileRef}
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
            <div
              className={clsx('link-cancel', isLoading && 'disabledLink')}
              role="button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (!isLoading) {
                  setIsLinkEditMode(false);
                  setSetting('isCreatingLink', false, false);
                  if (!editedLinkUrl) {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  }
                }
              }}
            >
              <CloseIcon />
            </div>
            <div
              className={clsx('link-confirm', isLoading && 'disabledLink')}
              role="button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={async () => {
                if (isLoading) return;
                setSetting('isCreatingLink', false, false);
                await handleLinkSubmission(editedLinkUrl, fileDescription);
              }}
            >
              <ConfirmIcon />
            </div>
          </div>
          {isLoading && (
            <div className={'loaderContainer'}>
              <div>
                {i18n.t('file_is_uploading')}: {uploadProgress ?? 0}%
              </div>
              <LinearProgress
                className="link-progress"
                variant="determinate"
                value={uploadProgress ?? 0}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="link-view">
          <AppLink to={sanitizeUrl(linkUrl)} target={'_blank'}>
            {linkUrl}
          </AppLink>
          <div className="link-container">
            <div
              className="link-edit"
              role="button"
              tabIndex={0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setEditedLinkUrl(linkUrl);
                setIsLinkEditMode(true);
              }}
            >
              <EditIcon />
            </div>
            {pageDesignerContext?.defaultLanguage ===
              pageDesignerContext?.pageEditorLanguage &&
              !isSelectionInsideTabLink && (
                <div
                  className="link-trash"
                  role="button"
                  tabIndex={0}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  }}
                >
                  <TrashIcon />
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>,
  linkEditorRef: React.RefObject<HTMLDivElement | null>,
): React.JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  const {
    settings: { isCreatingLink },
    setOption: setSetting,
  } = useSettings();

  useEffect(() => {
    function $updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(
          focusNode,
          $isAutoLinkNode,
        );
        if (!focusLinkNode && !focusAutoLinkNode) {
          setIsLink(false);
          setSetting('isCreatingLink', false, false);
          return;
        }
        const badNode = selection
          .getNodes()
          .filter((node) => !$isLineBreakNode(node))
          .find((node) => {
            const linkNode = $findMatchingParent(node, $isLinkNode);
            const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
            return (
              (focusLinkNode && !focusLinkNode.is(linkNode)) ||
              (linkNode && !linkNode.is(focusLinkNode)) ||
              (focusAutoLinkNode && !focusAutoLinkNode.is(autoLinkNode)) ||
              (autoLinkNode &&
                (!autoLinkNode.is(focusAutoLinkNode) ||
                  autoLinkNode.getIsUnlinked()))
            );
          });
        if (!badNode) {
          setIsLink(true);
        } else {
          setSetting('isCreatingLink', false, false);
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  useEffect(() => {
    if (isCreatingLink) {
      setIsLink(true);
    }
  }, [isCreatingLink]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
      linkEditorRef={linkEditorRef}
    />,
    anchorElem,
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
  linkEditorRef,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
  linkEditorRef: React.RefObject<HTMLDivElement | null>;
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(
    editor,
    anchorElem,
    isLinkEditMode,
    setIsLinkEditMode,
    linkEditorRef,
  );
}
